// The play-through bot (gate D1.3): from a fresh save, the main line (reach the town, meet the elder, clear the ruins'
// gate, beat the Hollow Warden, report back) and the three side quests (meadowbells for the child, ore for the smith,
// the shades on the road for the villager), plus a gift, through the same seams a player uses:
//   - it moves the hero with the move stick (relative to the held camera yaw) and teleports between objectives with the
//     tools' warp (src/world/teleport.ts: the chunks there loaded first), never inside a sim step;
//   - it talks by standing in front of a townsperson and pressing interact (the things' chooser picks them), then reads
//     the conversation and advances or chooses (the dialogue box's two actions), preferring the choices its plan names;
//   - it gathers with interact presses on the herbs, flowers and ore;
//   - it fights through combat's port: each step it turns the stick toward the nearest enemy and, in reach, asks the
//     fight for an attack toward it (the hero intent the player's attack press becomes); it reads a telegraphed wind-up
//     whose shape will cover it and runs out of it, dodging when the strike is close and it has the stamina (the arena
//     bot's reading of telegraphs, src/enemies/bot.ts). When the hero falls it waits for the respawn and warps back.
// Its logic runs between sim advances as generators (each yield: seconds of sim time to run), so a spec drives it in
// slices of wall time and reads its report.
import { forwardOf, rightOf } from "@rpg/controller";
import type { Runtime } from "@rpg/runtime";
import { HERO_ID } from "../enemies/fight";
import { inShape } from "../enemies/bot";
import type { Vec3 } from "../enemies/brains";
import type { WorldMount } from "../world/scene";
import { teleportHero } from "../world/teleport";
import type { StoryLayer, StoryNpcs } from "./module";

/** One phase's result. */
export interface BotPhase {
  name: string;
  ok: boolean;
  simSeconds: number;
  error: string | null;
}

/** The bot's report. */
export interface StoryBotReport {
  done: boolean;
  ok: boolean;
  phase: string | null;
  phases: BotPhase[];
  simSeconds: number;
  quests: Record<string, string>;
  choices: { npc: string; choice: string }[];
  talks: number;
  fights: { phase: string; defeats: number; heroFalls: number; attacks: number; dodges: number }[];
  gathered: string[];
}

type Step = Generator<number, void, void>;

/** How near the bot stands to swing (m past the target's body): inside the wand's 7 m bolt. */
const REACH = 5.5;

/** The bot over a story scene. */
export function storyBot(d: { m: WorldMount; layer: StoryLayer; runtime: Runtime; dispatch: (a: string, p?: unknown) => void; scenes: () => string[]; npcs: StoryNpcs }) {
  const { m, layer, runtime } = d;
  const story = layer.story;
  const conv = layer.conversations;
  const phases: BotPhase[] = [];
  const choices: { npc: string; choice: string }[] = [];
  const fights: StoryBotReport["fights"] = [];
  const gathered: string[] = [];
  let talks = 0;
  let current: { name: string; gen: Step; t0: number } | null = null;
  let plan: { name: string; run: () => Step }[] = [];
  let started = false;
  let planned = 0;
  let t0 = 0;
  const now = (): number => runtime.clock.time;

  const holdYaw = (yaw: number): void => {
    const r = m.rig();
    r.yawHeld = true;
    r.camera?.setYaw(yaw);
    r.control().cameraYaw = yaw;
  };
  const warp = (x: number, z: number, yaw: number): void => {
    teleportHero(m, x, z, yaw);
    holdYaw(yaw);
  };
  /** Steer the stick toward a world direction (a unit [x, z]) at `tilt`. */
  const steer = (dx: number, dz: number, tilt: number): void => {
    const r = m.rig();
    const yaw = r.control().cameraYaw;
    const f = forwardOf(yaw);
    const rt = rightOf(yaw);
    r.actions.setStick("move", { x: (dx * rt.x + dz * rt.z) * tilt, y: (dx * f.x + dz * f.z) * tilt });
  };
  const still = (): void => m.rig().actions.setStick("move", { x: 0, y: 0 });
  function* waitUntil(what: string, pred: () => boolean, timeout = 30): Step {
    const start = now();
    while (!pred()) {
      if (now() - start > timeout) throw new Error(`timed out: ${what}`);
      yield 0.1;
    }
  }

  // --- talking ---------------------------------------------------------------------------------------------------------
  // the town's townsfolk walk their routines (phase D joined): each try stands the hero in front of where one is now
  function* talkTo(id: string, prefer: readonly string[] = []): Step {
    // the chooser picks the nearest in front: with another townsperson close by (the villager by the merchant's stall)
    // each try stands at another bearing round the NPC, and presses only when the chooser has picked them
    for (let tries = 0; !(conv.active() && conv.state().npc === id); tries++) {
      if (tries >= 12) throw new Error(`no conversation with ${id}`);
      if (conv.active()) yield* endTalk();
      const at = d.npcs.at(id);
      if (!at) throw new Error(`${id} is out of sight (indoors)`);
      const yaw = d.npcs.yaw(id) + [0, 0.9, -0.9, 1.8, -1.8, Math.PI][tries % 6]!;
      const dist = tries < 6 ? 1.4 : 1.0;
      warp(at[0] + Math.sin(yaw) * dist, at[2] + Math.cos(yaw) * dist, yaw + Math.PI);
      yield 0.05;
      if (m.things().choice().id !== `npc:${id}`) continue;
      m.things().press = true;
      yield 0.1;
    }
    talks++;
    while (conv.active()) {
      const s = conv.state();
      if (s.waiting.length) {
        yield 0.25;
        continue;
      }
      if (s.beat?.kind === "line") d.dispatch("story/advance");
      else if (s.beat?.kind === "choices") {
        const ids = s.beat.choices.map((c) => c.id);
        let k = ids.findIndex((c) => prefer.some((p) => c.endsWith(p)));
        if (k < 0) k = ids.length - 1;
        choices.push({ npc: id, choice: ids[k]! });
        d.dispatch("story/choose", { n: k });
      }
      yield 0.05;
    }
  }

  /** A conversation opened with the wrong townsperson: through it to its end, taking the last choice (a goodbye). */
  function* endTalk(): Step {
    for (let i = 0; i < 60 && conv.active(); i++) {
      const s = conv.state();
      if (s.waiting.length) {
        yield 0.25;
        continue;
      }
      if (s.beat?.kind === "line") d.dispatch("story/advance");
      else if (s.beat?.kind === "choices") d.dispatch("story/choose", { n: s.beat.choices.length - 1 });
      yield 0.05;
    }
  }

  // --- gathering -------------------------------------------------------------------------------------------------------
  function* gather(ids: readonly string[], enough: () => boolean): Step {
    const th = m.things();
    for (const id of ids) {
      if (enough()) return;
      const at = th.at(id);
      // stand 0.9 m south of it, facing it
      warp(at[0], at[2] + 0.9, Math.PI);
      yield 0.1;
      th.press = true;
      yield 0.2;
      yield* waitUntil(`gathering ${id}`, () => th.state().acting === null, 10);
      gathered.push(id);
      yield 0.2;
    }
    if (!enough()) throw new Error(`gathered ${ids.join(", ")} and still not enough`);
  }

  // --- fighting --------------------------------------------------------------------------------------------------------
  function* fightUntil(name: string, done: () => boolean, at: [number, number], o: { radius?: number; timeout?: number; want?: (e: { id: number; kind: string }) => boolean } = {}): Step {
    const f = m.fight();
    const r = m.rig();
    const radius = o.radius ?? 30;
    const tally = { phase: name, defeats: 0, heroFalls: 0, attacks: 0, dodges: 0 };
    fights.push(tally);
    const startDefeats = story.conditions.fact("log.defeated") as number;
    let swingIn = 0;
    let lastAlive = true;
    const seen = new Map<string, number>();
    const tilt = r.tuning.hero.walkTilt;
    const goTo = (): void => {
      warp(at[0], at[1], Math.atan2(0, -1));
    };
    goTo();
    f.botDriving = true;
    // the wand: its bolts reach 7 m, so a shade that keeps its distance is still in reach (the weapon switch a press makes)
    f.combat.setWeapon(HERO_ID, "wand");
    r.drive = () => {
      const h = r.hero();
      const me: Vec3 = [h.feet.x, h.feet.y, h.feet.z];
      const hero = f.view(HERO_ID);
      if (!hero?.alive) return still();
      swingIn -= r.dt;
      const s = f.state();
      // a telegraph whose shape covers the hero: run out of it, and dodge when it is about to land
      let threat: { away: [number, number]; left: number } | null = null;
      for (const e of s.enemies) {
        const st = f.combatState().fighters[e.id];
        if (!e.alive || !st?.alive || !st.move) continue;
        const mv = f.combat.moves.get(st.move.id)!;
        if (!mv.telegraph || st.move.step >= mv.windup) continue;
        const off = mv.telegraph.offset ?? 0;
        const base = st.move.anchor ?? e.pos;
        const origin: Vec3 = [base[0] + Math.sin(st.move.yaw) * off, base[1], base[2] + Math.cos(st.move.yaw) * off];
        if (!inShape(mv.telegraph.shape, origin, st.move.yaw, me, r.tuning.hero.radius + 0.5)) continue;
        // a beat to read it, as a player would
        const key = `${e.id}:${s.steps - st.move.step}`;
        if (!seen.has(key)) seen.set(key, st.move.step);
        if (seen.size > 64) seen.delete(seen.keys().next().value!);
        if (st.move.step - seen.get(key)! < 0.15 / r.dt && st.move.step < mv.windup - 0.3 / r.dt) continue;
        const left = (mv.windup - st.move.step) * r.dt;
        const dx = me[0] - origin[0];
        const dz = me[2] - origin[2];
        const dd = Math.hypot(dx, dz) || 1;
        const away: [number, number] = mv.telegraph.shape.kind === "line" ? [Math.cos(st.move.yaw), -Math.sin(st.move.yaw)] : [dx / dd, dz / dd];
        if (!threat || left < threat.left) threat = { away, left };
      }
      if (threat) {
        steer(threat.away[0], threat.away[1], 1);
        if (threat.left <= 0.28 && hero.defending === "none" && hero.stamina >= f.content.combat.rules.dodge.cost) {
          f.heroIntent = { kind: "dodge", dir: [threat.away[0], 0, threat.away[1]] };
          tally.dodges++;
        }
        return;
      }
      // the objective's own enemies wherever they went (a shade keeps its distance), else the nearest live one near the
      // fight's place
      let best: { pos: Vec3; d: number; r: number } | null = null;
      for (const e of s.enemies) {
        if (!e.alive || !(o.want?.(e) ?? false) && Math.hypot(e.pos[0] - at[0], e.pos[2] - at[1]) > radius) continue;
        const dist = Math.hypot(e.pos[0] - me[0], e.pos[2] - me[2]);
        if (!best || dist < best.d) best = { pos: e.pos, d: dist, r: f.content.kinds[e.kind]!.body.radius };
      }
      if (!best) return still();
      const dx = best.pos[0] - me[0];
      const dz = best.pos[2] - me[2];
      const dd = Math.hypot(dx, dz) || 1;
      if (dd <= REACH + best.r) {
        still();
        if (!hero.move && swingIn <= 0) {
          f.heroIntent = { kind: "attack", dir: [dx / dd, 0, dz / dd] };
          tally.attacks++;
          swingIn = 0.35;
        }
      } else steer(dx / dd, dz / dd, dd > 6 ? 1 : tilt);
    };
    try {
      const start = now();
      while (!done()) {
        if (now() - start > (o.timeout ?? 240)) throw new Error(`timed out: ${name}`);
        const alive = f.view(HERO_ID)?.alive ?? false;
        if (!alive && lastAlive) tally.heroFalls++;
        if (alive && !lastAlive) goTo(); // back from the respawn
        lastAlive = alive;
        // nobody left near the place but not done yet: go back there (a chase may have dragged the hero away)
        const h = r.hero();
        const chasing = f.state().enemies.some((e) => e.alive && o.want?.(e));
        if (alive && !chasing && Math.hypot(h.feet.x - at[0], h.feet.z - at[1]) > radius + 10) goTo();
        yield 0.1;
      }
      // (a ragdoll still falling when the bot warps away goes with its chunk: the bodies module, phase D joined)
    } finally {
      r.drive = null;
      f.botDriving = false;
      still();
      tally.defeats = (story.conditions.fact("log.defeated") as number) - startDefeats;
    }
  }

  // --- the plan --------------------------------------------------------------------------------------------------------
  const objective = (): string | null => (story.status("main") === "active" ? (story.conditions.fact("quest.main.objective") as string) : null);
  const has = (item: string, n: number) => (): boolean => (story.conditions.fact(`item.${item}`) as number) >= n;
  const PLAN: { name: string; run: () => Step }[] = [
    {
      name: "the opening",
      *run() {
        // a new game, as the title's first play starts one (the page has already; a second ask changes nothing), then
        // the town's opening watched to its end
        d.dispatch("story/new-game", { opening: true });
        yield* waitUntil("the opening scene", () => d.scenes().length === 0 && story.status("main") === "active", 40);
      },
    },
    {
      name: "reach the town",
      *run() {
        // warp to the road short of the town, then walk in along it
        warp(32, -12, Math.atan2(52 - 32, -24 - -12));
        const r = m.rig();
        r.drive = () => {
          const h = r.hero();
          const dx = 54 - h.feet.x;
          const dz = -25 - h.feet.z;
          const dd = Math.hypot(dx, dz) || 1;
          steer(dx / dd, dz / dd, r.tuning.hero.walkTilt);
        };
        try {
          yield* waitUntil("reaching the town", () => objective() !== "reach-town", 30);
        } finally {
          r.drive = null;
          still();
        }
      },
    },
    {
      name: "meet the elder",
      *run() {
        yield* talkTo("elder", ["ask-help"]);
        yield* waitUntil("the elder's task", () => objective() === "clear-ruins", 5);
      },
    },
    {
      name: "clear the ruins' gate",
      *run() {
        yield* waitUntil("the gate's guards", () => layer.state().pending.length === 0, 30);
        yield* fightUntil("the ruins' gate", () => objective() !== "clear-ruins", [108, -98], { radius: 22, want: (e) => layer.state().tags[String(e.id)] === "ruins-guard" });
      },
    },
    {
      name: "beat the Hollow Warden",
      *run() {
        yield* fightUntil("the Warden", () => (story.conditions.fact("log.defeated.warden") as number) >= 1, [126, -124], { radius: 26, timeout: 400 });
        yield* waitUntil("the Warden's fall to count", () => objective() === "report", 5);
      },
    },
    {
      name: "report to the elder",
      *run() {
        yield* talkTo("elder", ["report-done"]);
        yield* waitUntil("the main line done", () => story.status("main") === "done", 5);
      },
    },
    {
      name: "meadowbells for the child",
      *run() {
        yield* talkTo("child", ["accept"]);
        yield* gather(["flower-1", "flower-2", "flower-3"], has("meadowbell", 3));
        yield* talkTo("child", ["give"]);
        yield* waitUntil("the flowers quest done", () => story.status("flowers") === "done", 5);
      },
    },
    {
      name: "ore for the smith",
      *run() {
        yield* talkTo("smith", ["accept"]);
        yield* gather(["ore-1", "ore-3", "ore-2"], has("copper-ore", 2));
        yield* talkTo("smith", ["hand"]);
        yield* waitUntil("the ore quest done", () => story.status("ore") === "done", 5);
      },
    },
    {
      name: "the shades on the road",
      *run() {
        yield* talkTo("villager", ["accept"]);
        yield* fightUntil("the shade camp", () => story.conditions.fact("quest.road.objective") !== "clear", [4, 26], { radius: 24, want: (e) => e.kind === "shade" && !layer.state().tags[String(e.id)] });
        yield* talkTo("villager", ["clear"]);
        yield* waitUntil("the road quest done", () => story.status("road") === "done", 5);
      },
    },
    {
      name: "a gift for the villager",
      *run() {
        yield* gather(["herb-1", "herb-2"], has("sunleaf", 1));
        yield* talkTo("villager", ["gift"]);
      },
    },
  ];

  return {
    /** Begin from the scene's state now (a fresh save: the opening). */
    start(o: { phases?: readonly string[] } = {}): void {
      plan = o.phases ? PLAN.filter((p) => o.phases!.includes(p.name)) : [...PLAN];
      planned = plan.length;
      phases.length = 0;
      choices.length = 0;
      fights.length = 0;
      gathered.length = 0;
      talks = 0;
      started = true;
      t0 = now();
      current = null;
    },
    /** Run up to `seconds` of sim time (in 0.05 to 0.25 s slices, undrawn). Returns whether the plan is done. */
    run(seconds: number): boolean {
      if (!started) throw new Error("story bot: not started");
      const end = now() + seconds;
      while (now() < end) {
        if (!current) {
          const next = plan.shift();
          if (!next) return true;
          current = { name: next.name, gen: next.run(), t0: now() };
        }
        let r: IteratorResult<number, void>;
        try {
          r = current.gen.next();
        } catch (e) {
          phases.push({ name: current.name, ok: false, simSeconds: now() - current.t0, error: (e as Error).message });
          // a failed phase ends the run (what comes after depends on it)
          plan = [];
          current = null;
          return true;
        }
        if (r.done) {
          phases.push({ name: current.name, ok: true, simSeconds: now() - current.t0, error: null });
          current = null;
          continue;
        }
        runtime.advance(Math.max(1000 / 120, r.value * 1000), { draw: false });
      }
      return !current && plan.length === 0;
    },
    report(): StoryBotReport {
      return {
        done: started && !current && plan.length === 0,
        ok: started && !current && plan.length === 0 && phases.length === planned && phases.every((p) => p.ok),
        phase: current?.name ?? null,
        phases: phases.map((p) => ({ ...p })),
        simSeconds: now() - t0,
        quests: Object.fromEntries(story.quests.list().map((q) => [q.id, story.status(q.id)])),
        choices: choices.map((c) => ({ ...c })),
        talks,
        fights: fights.map((f) => ({ ...f })),
        gathered: [...gathered],
      };
    },
  };
}
